// SOVRANOVA — region-aware crisis resources for the always-on lifeline.
// Mirrors backend/services/safety/SafetyService.js CRISIS_RESOURCES. Bundled (not
// fetched) so the lifeline stays ZERO-NETWORK and works fully offline and pre-auth.
// resolveRegion() picks the user's region offline from EXPLICIT choices only:
// explicit device choice -> stored profile region (written by App.jsx on profile
// load; persisted server-side since the international beta wave) -> GLOBAL.
// It never infers a region from browser locale, timezone, IP or device location
// and never falls back to a specific country (final pre-IB-D wave, Sep 2026).
// KEEP IN SYNC with the backend resource list.

// Verified 18 Aug 2026 against official sources. NEVER write an emergency number
// from memory — verify against the provider/government source and date the change.
// UK note: 999 is the emergency number. NHS 111 is a NON-emergency advice line and
// must never be presented as the emergency contact.
export const CRISIS_RESOURCES = {
  AU: { region: 'Australia',      primary: { label: 'Lifeline',                      number: '13 11 14',     tel: '131114' },     emergency: { label: 'Emergency', number: '000', tel: '000' },  text: 'Text 0477 13 11 14' },
  US: { region: 'United States',  primary: { label: '988 Suicide & Crisis Lifeline', number: '988',          tel: '988' },        emergency: { label: 'Emergency', number: '911', tel: '911' },  text: 'Text HOME to 741741' },
  UK: { region: 'United Kingdom', primary: { label: 'Samaritans',                    number: '116 123',      tel: '116123' },     emergency: { label: 'Emergency', number: '999', tel: '999' },  text: 'Text SHOUT to 85258' },
  NZ: { region: 'New Zealand',    primary: { label: 'Need to Talk?',                 number: '1737',         tel: '1737' },       emergency: { label: 'Emergency', number: '111', tel: '111' },  text: 'Call or text 1737' },
  IE: { region: 'Ireland',        primary: { label: 'Samaritans',                    number: '116 123',      tel: '116123' },     emergency: { label: 'Emergency', number: '112', tel: '112' },  text: 'Free-text HELLO to 50808 · Pieta 1800 247 247' },
  CA: { region: 'Canada',         primary: { label: '9-8-8 Suicide Crisis Helpline', number: '988',          tel: '988' },        emergency: { label: 'Emergency', number: '911', tel: '911' },  text: 'Call or text 988' },
  EU: { region: 'Europe',         primary: { label: 'Find a helpline near you',      number: 'findahelpline.com', tel: null },    emergency: { label: 'Emergency', number: '112', tel: '112' } },
  // Global fallback — used whenever the region is unknown. NEVER show another
  // country's numbers to someone we cannot place: an unknown user seeing "000"
  // is worse than an honest "call your local emergency number".
  GLOBAL: {
    region: 'Your area',
    primary: { label: 'Find a helpline in your country', number: 'findahelpline.com', tel: null },
    emergency: { label: 'Emergency', number: 'your local emergency number', tel: null },
  },
};

const DEFAULT_REGION = 'GLOBAL';

// Verified: findahelpline.com lists vetted services in 175+ countries.
// Every crisis surface shows this so nobody ever sees an empty screen.
export const HELPLINE_DIRECTORY_URL = 'https://findahelpline.com/';

export function getCrisisResources(region) {
  return CRISIS_RESOURCES[region] || CRISIS_RESOURCES[DEFAULT_REGION];
}

// The one sentence every conversational prompt uses to bridge a person in
// crisis to real human help. Built from the registry so no prompt can ever carry
// a hard-coded country's numbers (international beta wave, Sep 2026). GLOBAL /
// unknown users are pointed to the app's Urgent help button and their local
// emergency number — never another country's line.
const PRONOUNS = {
  they: { subj: 'they', poss: 'their', be: 'are', reach: 'reach' },
  she:  { subj: 'she',  poss: 'her',   be: 'is',  reach: 'reaches' },
  he:   { subj: 'he',   poss: 'his',   be: 'is',  reach: 'reaches' },
};
export function crisisBridgeLine(region, pronoun = 'they') {
  const p = PRONOUNS[pronoun] || PRONOUNS.they;
  const key = region && CRISIS_RESOURCES[region] ? region : DEFAULT_REGION;
  const res = CRISIS_RESOURCES[key];
  if (key === DEFAULT_REGION || !res.primary.tel) {
    return `- Warmly bridge to real human help as care, not dismissal: the Urgent help button in this app shows support services for ${p.poss} area, and ${p.subj} should call ${p.poss} local emergency number if ${p.subj} ${p.be} in immediate danger. Never quote a phone number from memory. Offer to stay while ${p.subj} ${p.reach} out.`;
  }
  return `- Warmly bridge to real human help as care, not dismissal: ${res.primary.label} ${res.primary.number}, or ${res.emergency.label} ${res.emergency.number} if ${p.subj} ${p.be} in immediate danger (${res.region}). Never quote any other phone number from memory. Offer to stay while ${p.subj} ${p.reach} out.`;
}

// Fully-offline region resolution from EXPLICIT sources only.
// Order: the user's own explicit choice -> stored profile region -> GLOBAL.
// Browser locale, timezone, IP and device location are deliberately NOT used:
// a device setting is not a statement of where a person is, and a wrong guess
// hands them another nation's emergency number. We NEVER fall back to a
// specific country: an unplaceable user gets GLOBAL (directory + "your local
// emergency number"), which is honest and still usable.
// Map a stored region code to a registry key. Handles ISO aliases and the
// jurisdiction picker's sub-national codes (US-IL, US-CA ...), which share a
// country's emergency services even though their AI-disclosure rules differ.
function toRegistryKey(raw) {
  if (!raw) return null;
  const key = String(raw).trim().toUpperCase();
  if (CRISIS_RESOURCES[key]) return key;
  if (key === 'GB' || key === 'ENG' || key === 'SCT' || key === 'WLS' || key === 'NIR') return 'UK';
  if (key === 'EIR') return 'IE';
  if (key.startsWith('US-')) return 'US';
  if (key.startsWith('CA-')) return 'CA';
  if (key.startsWith('AU-')) return 'AU';
  if (key.startsWith('GB-')) return 'UK';
  if (key === 'XX') return 'GLOBAL';   // picker's explicit "Other"
  return null;
}

export function resolveRegion() {
  // 1) The region the user chose themselves in Settings (RegionPicker writes
  //    `sovranova_region`). An explicit choice always outranks the profile copy
  //    — including a stale profile value after they have moved country.
  try {
    const chosen = toRegistryKey(localStorage.getItem('sovranova_region'));
    if (chosen) return chosen;
  } catch (_) {}

  // 2) The region on their profile, persisted by App.jsx on load.
  try {
    const stored = toRegistryKey(localStorage.getItem('sov_region'));
    if (stored) return stored;
  } catch (_) {}

  // 3) Nothing explicit -> GLOBAL. No locale / timezone / IP / device inference.
  return DEFAULT_REGION;
}
